
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Eye, EyeOff, Save, Link } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/hooks/use-toast";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface NavLink {
  id: string;
  path: string;
  label: string;
  visible: boolean;
  order: number;
}

const NavbarLinksAdmin = () => {
  const [navLinks, setNavLinks] = useState<NavLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchNavLinks();
  }, []);

  const fetchNavLinks = async () => {
    try {
      setIsLoading(true);
      
      // Try to get navigation links from database
      const { data, error } = await supabase
        .from('navigation_links')
        .select('*')
        .order('order', { ascending: true });
      
      if (error) {
        throw error;
      }
      
      if (data && data.length > 0) {
        setNavLinks(data as NavLink[]);
      } else {
        // If no data exists, initialize with default navigation items
        const defaultNavLinks = [
          { path: "/", label: "Home", visible: true, order: 0 },
          { path: "/about", label: "About", visible: true, order: 1 },
          { path: "/courses", label: "Courses", visible: true, order: 2 },
          { path: "/blog", label: "Blog", visible: true, order: 3 },
          { path: "/freelance-toolkit", label: "Toolkit", visible: true, order: 4 },
          { path: "/contact", label: "Contact", visible: true, order: 5 }
        ];
        
        // Initialize the database with default values
        await Promise.all(
          defaultNavLinks.map(async (link) => {
            await supabase.from('navigation_links').insert([link]);
          })
        );
        
        setNavLinks(defaultNavLinks as NavLink[]);
      }
    } catch (error: any) {
      console.error("Error fetching navigation links:", error.message);
      toast({
        title: "Error loading navigation links",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVisibilityToggle = (id: string) => {
    setNavLinks(prevLinks => 
      prevLinks.map(link => 
        link.id === id ? { ...link, visible: !link.visible } : link
      )
    );
  };

  const saveChanges = async () => {
    try {
      setIsSaving(true);
      
      // Update each navigation link
      for (const link of navLinks) {
        const { error } = await supabase
          .from('navigation_links')
          .update({ visible: link.visible })
          .eq('id', link.id);
          
        if (error) throw error;
      }
      
      toast({
        title: "Navigation settings saved",
        description: "The navigation bar has been updated successfully.",
      });
    } catch (error: any) {
      console.error("Error saving navigation settings:", error.message);
      toast({
        title: "Error saving changes",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-40">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link className="h-5 w-5" />
            Navigation Links
          </CardTitle>
          <CardDescription>
            Control which pages appear in the navigation bar. Toggle visibility for each link.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-12 gap-4 font-medium text-sm text-muted-foreground pb-2">
              <div className="col-span-5 sm:col-span-4">Page</div>
              <div className="col-span-5 sm:col-span-6">Path</div>
              <div className="col-span-2 text-center">Visible</div>
            </div>
            <Separator />
            {navLinks.map((link) => (
              <div key={link.id} className="grid grid-cols-12 gap-4 items-center py-2">
                <div className="col-span-5 sm:col-span-4 font-medium">
                  {link.label}
                </div>
                <div className="col-span-5 sm:col-span-6 text-sm text-muted-foreground truncate">
                  {link.path}
                </div>
                <div className="col-span-2 flex justify-center">
                  <div className="flex items-center">
                    <Switch 
                      checked={link.visible} 
                      onCheckedChange={() => handleVisibilityToggle(link.id)}
                      aria-label={`Toggle visibility for ${link.label}`}
                    />
                    {link.visible ? 
                      <Eye className="h-4 w-4 ml-2 text-green-500" /> : 
                      <EyeOff className="h-4 w-4 ml-2 text-gray-400" />
                    }
                  </div>
                </div>
              </div>
            ))}
            <div className="flex justify-end mt-4">
              <Button 
                onClick={saveChanges} 
                disabled={isSaving}
                className="flex items-center gap-2"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save Changes
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NavbarLinksAdmin;
